'use client';

import React, { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface Milestone {
  year: string;
  title: string;
  description: string;
  icon: string;
  color: string;
}

interface TimelineSectionProps {
  milestones: Milestone[];
}

const TimelineSection: React.FC<TimelineSectionProps> = ({ milestones }) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  if (!isHydrated) {
    return (
      <section className="py-16 lg:py-24 bg-card">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-heading font-bold text-foreground mb-4">
              Our Journey
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Milestones that mark our commitment to serving the community
            </p>
          </div>
          <div className="max-w-4xl mx-auto space-y-8">
            {milestones.map((_, index) => (
              <div key={index} className="bg-background p-6 rounded-xl shadow-subtle">
                <div className="h-24 bg-muted animate-pulse rounded" />
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  const getColorClasses = (color: string) => {
    const colorMap: Record<string, { bg: string; text: string; border: string }> = {
      primary: { bg: 'bg-primary/10', text: 'text-primary', border: 'border-primary' },
      secondary: { bg: 'bg-secondary/10', text: 'text-secondary', border: 'border-secondary' },
      accent: { bg: 'bg-accent/10', text: 'text-accent', border: 'border-accent' },
      supporting: { bg: 'bg-supporting/10', text: 'text-supporting', border: 'border-supporting' },
    };
    return colorMap[color] || colorMap.primary;
  };

  return (
    <section className="py-16 lg:py-24 bg-card">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-3xl lg:text-4xl font-heading font-bold text-foreground mb-4">
            Our Journey
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Milestones that mark our commitment to serving the community
          </p>
        </div>

        <div className="max-w-4xl mx-auto">
          <div className="relative">
            <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-border hidden lg:block" />
            
            <div className="space-y-8">
              {milestones.map((milestone, index) => {
                const colors = getColorClasses(milestone.color);
                const isActive = activeIndex === index;
                
                return (
                  <div
                    key={index}
                    className={`relative bg-background p-6 rounded-xl shadow-subtle hover:shadow-elevated transition-all duration-300 cursor-pointer ${
                      isActive ? 'ring-2 ring-primary' : ''
                    }`}
                    onClick={() => setActiveIndex(index)}
                  >
                    <div className="flex items-start space-x-4">
                      <div className={`w-16 h-16 ${colors.bg} rounded-full flex items-center justify-center flex-shrink-0 ${isActive ? 'scale-110' : ''} transition-transform duration-300`}>
                        <Icon name={milestone.icon as any} size={28} className={colors.text} />
                      </div>
                      
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-2">
                          <span className={`text-sm font-semibold ${colors.text} px-3 py-1 ${colors.bg} rounded-full`}>
                            {milestone.year}
                          </span>
                        </div>
                        <h3 className="text-xl font-heading font-semibold text-foreground mb-2">
                          {milestone.title}
                        </h3>
                        <p className="text-muted-foreground leading-relaxed">
                          {milestone.description}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TimelineSection;